/*
  The thin client only has basic functionality like getting the events and uuid's.
  This prevents having to include extra dependencies that are only needed for actual backend communication.
  This is used by the partner iframes.
 */

import {
  getSolvariClientId,
  getSolvariSessionId,
  sSessionStorage,
} from "@solvari/utils";

import type { ArgusEvent } from "@/service/backendSync.ts";

declare global {
  // oxlint-disable-next-line typescript/consistent-type-definitions
  interface SessionStorage {
    argusEvents: ArgusEvent[];
  }
}

function getArgusEvents() {
  return sSessionStorage.getItem("argusEvents");
}

function argusSession() {
  return {
    sessionUuid: getSolvariSessionId(),
    clientId: getSolvariClientId(),
  };
}

function shouldApplyArgusSession(link: string) {
  try {
    const url = new URL(link);
    return (
      (url.protocol === "http:" || url.protocol === "https:") &&
      url.hostname !== window.location.hostname
    );
  } catch (_error) {
    return false;
  }
}

function applyArgusUuidToUrl(link: string) {
  if (!shouldApplyArgusSession(link)) {
    return link;
  }

  const url = new URL(link);
  const session = argusSession();

  if (session.sessionUuid) {
    url.searchParams.set("solvariSession", session.sessionUuid);
  }
  if (session.clientId) {
    url.searchParams.set("solvariClientId", session.clientId);
  }

  return url.href;
}

function redirectWithTracking(link: string) {
  const url = applyArgusUuidToUrl(link);

  try {
    const anchor = document.createElement("a");
    anchor.href = url;
    anchor.id = "tracking-redirect";
    document.body.append(anchor);
    anchor.click();
  } catch (_error) {
    window.location.href = url;
  }
}

export {
  applyArgusUuidToUrl,
  argusSession,
  getArgusEvents,
  redirectWithTracking,
  shouldApplyArgusSession,
};
